/**
 * Merchant POS Feedback Page
 * ==========================
 * 
 * Merchant-specific view of their customer feedback.
 */

import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Star,
  Download,
  TrendingUp,
  MessageSquare,
  Filter,
  RefreshCw,
  Loader2,
  Sparkles,
  QrCode,
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const POSFeedbackPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [ratingFilter, setRatingFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      let url = `${API}/feedback/merchant?limit=100`;
      if (ratingFilter && ratingFilter !== "all") url += `&rating=${ratingFilter}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFeedback(data.feedback || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error("Error fetching feedback:", err);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API}/feedback/merchant/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error("Error fetching analytics:", err);
    }
  };

  useEffect(() => {
    fetchFeedback();
    fetchAnalytics();
  }, [ratingFilter, fromDate, toDate]);

  const handleExport = async () => {
    try {
      let url = `${API}/feedback/merchant/export`;
      const params = [];
      if (fromDate) params.push(`from_date=${fromDate}`);
      if (toDate) params.push(`to_date=${toDate}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `my_feedback_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Export downloaded");
      }
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export");
    }
  };

  const getRatingBadge = (rating) => {
    if (rating >= 4) return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
    if (rating >= 3) return "bg-amber-500/20 text-amber-400 border-amber-500/30";
    return "bg-red-500/20 text-red-400 border-red-500/30";
  };

  const renderStars = (rating, size = "h-4 w-4") => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= rating 
                ? "fill-amber-400 text-amber-400 drop-shadow-[0_0_3px_rgba(251,191,36,0.5)]" 
                : "text-slate-600"
            }`}
          />
        ))}
      </div>
    );
  };

  // Rating distribution
  const ratingDist = analytics?.rating_distribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  const totalRatings = Object.values(ratingDist).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/merchant")}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-indigo-400" />
                Customer Feedback
              </h1>
              <p className="text-gray-500 text-sm">See what your customers are saying</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => { fetchFeedback(); fetchAnalytics(); }} 
              className="bg-white/5 border-gray-200 text-gray-900 hover:bg-white/20"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleExport} className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <MessageSquare className="h-5 w-5 text-indigo-600" />
              </div>
              <span className="text-gray-500 text-sm font-medium">Total Reviews</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{analytics?.total_feedback || 0}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Star className="h-5 w-5 text-amber-500" />
              </div>
              <span className="text-gray-500 text-sm font-medium">Average Rating</span>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-3xl font-bold text-slate-800">{analytics?.avg_rating || "-"}</p>
              {analytics?.avg_rating && renderStars(Math.round(analytics.avg_rating), "h-5 w-5")}
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <ThumbsUp className="h-5 w-5 text-emerald-600" />
              </div>
              <span className="text-gray-500 text-sm font-medium">Positive (4-5★)</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">
              {(ratingDist["4"] || 0) + (ratingDist["5"] || 0)}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-red-100">
                <ThumbsDown className="h-5 w-5 text-red-500" />
              </div>
              <span className="text-gray-500 text-sm font-medium">Needs Attention (1-2★)</span>
            </div>
            <p className="text-3xl font-bold text-red-500">
              {(ratingDist["1"] || 0) + (ratingDist["2"] || 0)}
            </p>
          </div>
        </div>

        {/* Rating Distribution + Filters */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Rating Distribution */}
          <div className="bg-white rounded-2xl p-5 shadow-lg">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              Rating Breakdown
            </h3>
            {totalRatings > 0 ? (
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map(star => {
                  const count = ratingDist[star] || 0;
                  const percent = totalRatings > 0 ? (count / totalRatings * 100) : 0;
                  const barColor = star >= 4 ? "bg-emerald-500" : star === 3 ? "bg-amber-500" : "bg-red-500";
                  return (
                    <div key={star} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-slate-600 w-8">{star}★</span>
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${barColor} rounded-full transition-all duration-500`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-500 w-10 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">No ratings yet</p>
            )}
          </div>

          {/* Filters */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-5 shadow-lg">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Filter className="h-4 w-4 text-indigo-500" />
              Filter Reviews
            </h3>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-36">
                <label className="text-xs text-gray-500 block mb-1.5 font-medium">Rating</label>
                <Select value={ratingFilter} onValueChange={setRatingFilter}>
                  <SelectTrigger className="bg-slate-50 border-slate-200">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ratings</SelectItem>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                    <SelectItem value="2">2 Stars</SelectItem>
                    <SelectItem value="1">1 Star</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-40">
                <label className="text-xs text-gray-500 block mb-1.5 font-medium">From</label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="bg-slate-50 border-slate-200"
                />
              </div>
              
              <div className="w-40">
                <label className="text-xs text-gray-500 block mb-1.5 font-medium">To</label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="bg-slate-50 border-slate-200"
                />
              </div>
              
              <Button
                variant="outline"
                onClick={() => {
                  setRatingFilter("all");
                  setFromDate("");
                  setToDate("");
                }}
                className="border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        {/* Feedback List */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Customer Reviews ({total})</h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          ) : feedback.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
                <QrCode className="h-10 w-10 text-indigo-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">No feedback yet</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Customers can scan the QR code on their receipt to leave feedback. Start collecting reviews today!
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {feedback.map((fb) => (
                <div key={fb.feedback_id} className="p-5 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-start gap-4">
                    {/* Rating Badge */}
                    <div className={`px-3 py-2 rounded-xl border ${getRatingBadge(fb.rating)} flex flex-col items-center min-w-[70px]`}>
                      <span className="text-2xl font-bold">{fb.rating}</span>
                      <div className="flex gap-0.5 mt-1">
                        {renderStars(fb.rating, "h-3 w-3")}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm text-gray-500">
                          {new Date(fb.created_at).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </span>
                      </div>
                      {fb.comment ? (
                        <p className="text-slate-700 leading-relaxed">"{fb.comment}"</p>
                      ) : (
                        <p className="text-gray-500 italic text-sm">No comment provided</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2 font-mono">
                        Order: {fb.transaction_id}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default POSFeedbackPage;
